/**
 * AURA Field — Entry Point
 *
 * Auth flow:
 *  1. Initialize Sentry (before anything else)
 *  2. Initialize Clerk auth
 *  3. If already signed in → enter field immediately
 *  4. If not → show auth gate with Clerk sign-in UI + anonymous skip
 *  5. On sign-in or skip → fade gate, launch field
 */

import * as Sentry from '@sentry/browser';
import { Field } from './scene/field';
import { initAuth, mountSignIn, unmountSignIn, onAuthChange, isAuthConfigured } from './api/auth';
import { Dashboard } from './components/Dashboard';
import { Intro } from './interaction/intro';

// ────────────────────────────────────────────────────────
// Sentry — error tracking (initialized before everything)
// ────────────────────────────────────────────────────────
Sentry.init({
  dsn: 'https://51dd0da04bbad0af5747bcffb4e3af4f@o4510844831334400.ingest.us.sentry.io/4510849377566720',
  environment: import.meta.env.MODE, // 'development' | 'production'
  enabled: import.meta.env.PROD,     // only send in production builds
  tracesSampleRate: 0.2,             // 20% of transactions for performance
  replaysSessionSampleRate: 0,       // no session replays by default
  replaysOnErrorSampleRate: 0.5,     // 50% replay capture on errors
});

// ────────────────────────────────────────────────────────
// DOM refs
// ────────────────────────────────────────────────────────
const passwordGate = document.getElementById('password-gate')!;
const pwInput = document.getElementById('pw-input') as HTMLInputElement;
const pwEnter = document.getElementById('pw-enter')!;
const pwError = document.getElementById('pw-error')!;
const gate = document.getElementById('auth-gate')!;
const clerkMount = document.getElementById('clerk-signin')!;
const skipBtn = document.getElementById('auth-skip')!;
const fieldRoot = document.getElementById('field-root')!;

let field: Field | null = null;
let entered = false;

// ────────────────────────────────────────────────────────
// Password gate — private preview access control
// ────────────────────────────────────────────────────────
const ACCESS_CODE = import.meta.env.VITE_ACCESS_CODE || 'aura2026!';
const SESSION_KEY = 'aura_access_granted';

function checkPasswordGate(): Promise<void> {
  // If already passed in this session, skip
  if (sessionStorage.getItem(SESSION_KEY) === '1') {
    passwordGate.classList.add('hidden');
    setTimeout(() => { passwordGate.style.display = 'none'; }, 800);
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    function tryAccess() {
      const code = pwInput.value.trim();
      if (code === ACCESS_CODE) {
        sessionStorage.setItem(SESSION_KEY, '1');
        passwordGate.classList.add('hidden');
        setTimeout(() => { passwordGate.style.display = 'none'; }, 800);
        resolve();
      } else {
        pwInput.classList.add('error');
        pwError.textContent = code ? 'Invalid access code' : '';
        setTimeout(() => pwInput.classList.remove('error'), 1500);
      }
    }

    pwEnter.addEventListener('click', tryAccess);
    pwInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') tryAccess();
    });
  });
}

// ────────────────────────────────────────────────────────
// Enter field — hide gate, boot experience
// ────────────────────────────────────────────────────────
function enterField(): void {
  if (entered) return;
  entered = true;

  // Fade out auth gate
  gate.classList.add('hidden');
  setTimeout(() => {
    gate.style.display = 'none';
    if (clerkMount) unmountSignIn(clerkMount);
  }, 800);

  // Show intro animation (plays while field loads in background)
  // or progress bar if intro was already seen
  const runEntry = async () => {
    if (Intro.shouldShow()) {
      const intro = new Intro();
      // Start loading in background immediately while intro plays
      const fieldLoadPromise = bootField();
      await intro.run(); // waits for intro to complete or be skipped
      await fieldLoadPromise; // ensure field is ready
    } else {
      showLoadingProgress();
      await bootField();
    }
  };

  runEntry().catch(console.error);
}

async function bootField(): Promise<void> {
  try {
    field = new Field(fieldRoot);
    new Dashboard();
    initFirstLoadOverlay();
    initQueryHintRotation();
  } catch (error) {
    Sentry.captureException(error, { tags: { component: 'field-init' } });
    console.error('Field initialization failed:', error);
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;top:20px;left:20px;color:#ff4444;font-family:monospace;font-size:14px;z-index:9999;background:#111;padding:20px;border-radius:8px;max-width:80vw;white-space:pre-wrap;';
    el.textContent = `Field initialization failed:\n${error}`;
    document.body.appendChild(el);
  }
}

// ────────────────────────────────────────────────────────
// Loading progress — shown on first visit while 3D field initializes
// ────────────────────────────────────────────────────────
function showLoadingProgress(): void {
  const el = document.createElement('div');
  el.id = 'aura-loading';
  el.style.cssText = `
    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
    z-index: 200; font-family: 'Inter', sans-serif; text-align: center;
    pointer-events: none;
  `;

  const STEPS = [
    'Connecting to the intelligence field…',
    'Loading 12 signal clusters…',
    'Reading live signals…',
    'Generating intelligence brief…',
    'Field is ready.',
  ];

  let step = 0;
  const label = document.createElement('div');
  label.style.cssText = `font-size: 10px; color: rgba(255,255,255,0.30); letter-spacing: 0.08em; margin-bottom: 8px;`;
  label.textContent = STEPS[0];

  const bar = document.createElement('div');
  bar.style.cssText = `width: 200px; height: 1px; background: rgba(255,255,255,0.08); border-radius: 1px; overflow: hidden;`;
  const fill = document.createElement('div');
  fill.style.cssText = `height: 100%; background: rgba(255,255,255,0.35); border-radius: 1px; width: 0; transition: width 0.6s ease;`;
  bar.appendChild(fill);

  el.appendChild(label);
  el.appendChild(bar);
  document.body.appendChild(el);

  // Advance progress
  const advance = () => {
    step = Math.min(step + 1, STEPS.length - 1);
    label.textContent = STEPS[step];
    fill.style.width = `${(step / (STEPS.length - 1)) * 100}%`;
  };

  const timers = [
    setTimeout(advance, 800),
    setTimeout(advance, 2000),
    setTimeout(advance, 3500),
    setTimeout(advance, 5000),
  ];

  // Remove after 8s (field should be loaded by then)
  setTimeout(() => {
    el.style.transition = 'opacity 0.8s ease';
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 900);
    timers.forEach(clearTimeout);
  }, 8000);
}

// ────────────────────────────────────────────────────────
// Finding 1: First-load field explanation overlay
// Three lines, 8s auto-dismiss, fades in with the field.
// ────────────────────────────────────────────────────────
function initFirstLoadOverlay(): void {
  if (sessionStorage.getItem('aura_intro_seen')) return;
  sessionStorage.setItem('aura_intro_seen', '1');

  const el = document.createElement('div');
  el.style.cssText = `
    position: fixed; bottom: 120px; left: 50%; transform: translateX(-50%);
    text-align: center; z-index: 50; pointer-events: none;
    font-family: 'Inter', -apple-system, sans-serif;
    opacity: 0; transition: opacity 1.2s ease;
  `;
  el.innerHTML = `
    <div style="font-size:13px;font-weight:400;color:rgba(255,255,255,0.55);line-height:1.9;letter-spacing:0.01em;">
      Signal clusters have mass.<br>
      The heaviest ideas pull others toward them.<br>
      <span style="color:rgba(255,255,255,0.30);font-size:11px;">Press / to ask, or hover to explore.</span>
    </div>
  `;
  document.body.appendChild(el);

  setTimeout(() => { el.style.opacity = '1'; }, 2200); // after entry ritual
  setTimeout(() => {
    el.style.transition = 'opacity 1.5s ease';
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 1600);
  }, 9000);
}

// ────────────────────────────────────────────────────────
// Finding 4: Rotating query hint — cycles through example prompts
// ────────────────────────────────────────────────────────
function initQueryHintRotation(): void {
  const hint = document.getElementById('query-hint');
  if (!hint) return;

  const PROMPTS = [
    'What cultural signals should Soho be watching right now?',
    'Where is the white space in experiential formats?',
    'Which cluster is rising fastest this week?',
    'What would you activate around right now?',
    'Ask the field',
  ];

  const keyEl = hint.querySelector('.query-hint-key');
  const textNode = hint.childNodes[hint.childNodes.length - 1] as Text;
  let idx = 0;

  setInterval(() => {
    // Only rotate when hint is visible (not when query is open)
    if (!hint.classList.contains('visible')) return;
    idx = (idx + 1) % PROMPTS.length;
    hint.style.transition = 'opacity 0.4s ease';
    hint.style.opacity = '0';
    setTimeout(() => {
      if (textNode) textNode.textContent = ' ' + PROMPTS[idx];
      hint.style.opacity = '1';
    }, 420);
  }, 8000);
}

// ────────────────────────────────────────────────────────
// Main boot sequence
// ────────────────────────────────────────────────────────
async function boot(): Promise<void> {
  // Password gate first (private preview)
  await checkPasswordGate();

  // If Clerk isn't configured, skip auth entirely
  if (!isAuthConfigured()) {
    enterField();
    return;
  }

  // Initialize Clerk and check for existing session
  const state = await initAuth();

  if (state.isAuthenticated) {
    // Tag Sentry with user identity
    Sentry.setUser({ id: state.userId ?? undefined });
    // Already signed in — go straight to field
    enterField();
    return;
  }

  // Not signed in — show auth gate with Clerk UI
  await mountSignIn(clerkMount);

  // Listen for successful auth
  const unsubscribe = onAuthChange((authState) => {
    if (authState.isAuthenticated && !entered) {
      Sentry.setUser({ id: authState.userId ?? undefined });
      unsubscribe();
      enterField();
    }
  });

  // Anonymous skip
  skipBtn.addEventListener('click', () => {
    enterField();
  });
}

boot().catch((err) => {
  Sentry.captureException(err, { tags: { component: 'boot' } });
  console.error('Boot failed:', err);
  // Fallback — enter field without auth
  enterField();
});

// Hot module replacement
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    field?.dispose();
  });
}
